import type { PosterField } from '../api/types';

/**
 * Renders one field of a template's data model. List values are held as a newline-separated
 * string, because the order entered is the order printed down the poster.
 */
export default function PosterFieldInput({
  field,
  value,
  error,
  onChange,
}: {
  field: PosterField;
  value: string;
  error?: string;
  onChange: (value: string) => void;
}) {
  const describedBy = field.helpText || error ? `${field.key}-hint` : undefined;

  const hint = (
    <>
      {error && (
        <small id={`${field.key}-hint`} className="field__error">
          {error}
        </small>
      )}
      {!error && field.helpText && (
        <small id={`${field.key}-hint`} className="muted">
          {field.helpText}
        </small>
      )}
    </>
  );

  if (field.type === 'List') {
    const items = value.length > 0 ? value.split('\n') : [''];
    const commit = (next: string[]) => onChange(next.filter((item, i) => item.trim() !== '' || i < next.length).join('\n'));

    const move = (index: number, delta: number) => {
      const target = index + delta;
      if (target < 0 || target >= items.length) return;
      const next = [...items];
      const moved = next[index]!;
      next[index] = next[target]!;
      next[target] = moved;
      commit(next);
    };

    return (
      <fieldset className="field field--list">
        <legend>
          {field.label}
          {field.required && <span className="field__required" aria-hidden="true"> *</span>}
        </legend>
        {hint}

        {items.map((item, index) => (
          <div key={index} className="players__row">
            <span className="players__number">{index + 1}</span>
            <input
              value={item}
              placeholder={field.placeholder ?? ''}
              maxLength={field.maxLength ?? undefined}
              onChange={(e) => {
                const next = [...items];
                next[index] = e.target.value;
                onChange(next.join('\n'));
              }}
            />
            <div className="players__controls">
              <button
                type="button"
                className="button button--ghost"
                aria-label={`Move entry ${index + 1} up`}
                disabled={index === 0}
                onClick={() => move(index, -1)}
              >
                ↑
              </button>
              <button
                type="button"
                className="button button--ghost"
                aria-label={`Move entry ${index + 1} down`}
                disabled={index === items.length - 1}
                onClick={() => move(index, 1)}
              >
                ↓
              </button>
              <button
                type="button"
                className="button button--ghost"
                aria-label={`Remove entry ${index + 1}`}
                onClick={() => commit(items.filter((_, i) => i !== index))}
              >
                Remove
              </button>
            </div>
          </div>
        ))}

        <button
          type="button"
          className="button button--ghost"
          disabled={field.maxItems != null && items.length >= field.maxItems}
          onClick={() => onChange([...items, ''].join('\n'))}
        >
          Add {field.label.toLowerCase()}
        </button>
      </fieldset>
    );
  }

  return (
    <label className={`field ${field.type === 'MultilineText' ? 'span-2' : ''}`}>
      <span className="field__label">
        {field.label}
        {field.required && <span className="field__required" aria-hidden="true"> *</span>}
      </span>

      {field.type === 'Choice' ? (
        <select
          value={value}
          aria-describedby={describedBy}
          onChange={(e) => onChange(e.target.value)}
        >
          {(field.options ?? []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : field.type === 'MultilineText' ? (
        <textarea
          rows={2}
          value={value}
          placeholder={field.placeholder ?? ''}
          maxLength={field.maxLength ?? undefined}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          type={field.type === 'Date' ? 'date' : field.type === 'Time' ? 'time' : 'text'}
          value={value}
          placeholder={field.placeholder ?? ''}
          maxLength={field.maxLength ?? undefined}
          required={field.required}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      {hint}
    </label>
  );
}
